import { useCallback, useEffect, useState } from 'react';
import { useMsal, useAccount } from '@azure/msal-react';
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from './msalConfig';
import { AuthUser, UserRole } from '../types/index';
import client from '../api/client';

interface UseAuthReturn {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string>;
}

interface MeResponse {
  id: string;
  entra_oid: string;
  display_name: string;
  email: string;
  job_title?: string | null;
  department?: string | null;
  profile_photo_url?: string | null;
  ou_memberships?: string[];
}

interface AccessGroupSummary {
  entra_object_id: string;
  roles: string[];
  is_active: boolean;
}

// Bug #318: this used to be a hand-maintained `Record<string, UserRole>`
// listing the six roles that existed when Feature #256 landed. Epic #281
// added sm_admin and sm_manager to `UserRole` and to the backend but not to
// that second list, so both were mapped to `undefined` and then dropped by a
// `.filter(Boolean)`. The backend granted them, the frontend forgot them, and
// every SM route guard bounced a legitimate sm_admin/sm_manager to Home while
// still showing them the menu items (nav visibility is decided server-side).
//
// `UserRole` is now the single source of truth: its values already mirror
// backend app/auth/roles.py, so a role added there flows through here with no
// second edit. Do not reintroduce a parallel list.
const KNOWN_ROLE_VALUES = new Set<string>(Object.values(UserRole));

export const useAuth = (): UseAuthReturn => {
  const { instance, accounts, inProgress } = useMsal();
  const account = useAccount(accounts[0] || null);

  const isAuthenticated = accounts.length > 0;
  const msalLoading =
    inProgress === InteractionStatus.Login ||
    inProgress === InteractionStatus.AcquireToken ||
    inProgress === InteractionStatus.Startup ||
    inProgress === InteractionStatus.HandleRedirect;

  const [profile, setProfile] = useState<AuthUser | null>(null);
  const [profileLoading, setProfileLoading] = useState<boolean>(isAuthenticated);

  useEffect(() => {
    let cancelled = false;

    if (!isAuthenticated || !account) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }

    setProfileLoading(true);
    (async () => {
      try {
        const [meRes, groupsRes] = await Promise.all([
          client.get<MeResponse>('/auth/me'),
          // Bug #318: page_size is explicit because this endpoint paginates
          // at 20 by default, ordered by display name. Without it, a user
          // whose access group sorts past the 20th active group silently
          // loses every role that group carries — the same failure mode as
          // Bug #315 and Bug #307. 100 is the schema's ceiling (see
          // PaginationParams), so this raises the cliff rather than removing
          // it; if the group count ever approaches 100, resolve roles from a
          // dedicated endpoint instead of paging this list.
          client.get<{ items: AccessGroupSummary[]; total: number }>(
            '/access-groups?include_inactive=false&page_size=100'
          ),
        ]);
        if (cancelled) return;
        const me = meRes.data;
        const ous = (me.ou_memberships || []).map((o) => o.toLowerCase());
        const effectiveRoleStrings = new Set<string>();
        for (const g of groupsRes.data.items || []) {
          if (!g.is_active) continue;
          if (ous.includes(g.entra_object_id.toLowerCase())) {
            for (const r of g.roles || []) effectiveRoleStrings.add(r);
          }
        }
        const roles: UserRole[] = [];
        for (const roleString of effectiveRoleStrings) {
          if (KNOWN_ROLE_VALUES.has(roleString)) {
            roles.push(roleString as UserRole);
          } else {
            // Bug #318: never drop a granted role in silence. An unrecognised
            // string here means the backend grants something this build has
            // no concept of, which the user experiences as an unexplained
            // redirect with a clean console — precisely what made the SM
            // roles take a user report to find.
            console.warn(
              `Access group grants role "${roleString}", which this frontend ` +
                'build does not recognise; it will be ignored. Add it to ' +
                'UserRole in types/index.ts.'
            );
          }
        }

        if (groupsRes.data.total > (groupsRes.data.items || []).length) {
          // Defence in depth for the pagination note above: if the page ever
          // does truncate, say so rather than silently resolving a partial
          // role set.
          console.warn(
            `Resolved roles from ${(groupsRes.data.items || []).length} of ` +
              `${groupsRes.data.total} access groups — the list was truncated, ` +
              'so some granted roles may be missing.'
          );
        }

        setProfile({
          id: me.id,
          entraOid: me.entra_oid,
          displayName: me.display_name,
          email: me.email,
          jobTitle: me.job_title || undefined,
          department: me.department || undefined,
          profilePhotoUrl: me.profile_photo_url || undefined,
          roles,
          ouMemberships: me.ou_memberships || [],
        });
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load user profile/roles', err);
          // Fall back to the MSAL-derived shell so the app still renders,
          // with no roles — AdminRoute will redirect, which is correct.
          setProfile({
            id: account.localAccountId || '',
            entraOid: (account.idTokenClaims as any)?.oid || '',
            displayName: account.name || '',
            email: account.username || '',
            roles: [],
            ouMemberships: [],
          });
        }
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, account?.localAccountId]);

  const isLoading = msalLoading || (isAuthenticated && profileLoading && !profile);

  const login = useCallback(async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }, [instance]);

  const logout = useCallback(async () => {
    try {
      await instance.logoutRedirect();
    } catch (error) {
      console.error('Logout error:', error);
    }
  }, [instance]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    if (!account) {
      throw new Error('No account found');
    }

    try {
      const response = await instance.acquireTokenSilent({
        scopes: loginRequest.scopes,
        account,
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        const result = await instance.acquireTokenPopup(loginRequest);
        return result.accessToken;
      }
      throw error;
    }
  }, [instance, account]);

  return {
    user: profile,
    isAuthenticated,
    isLoading,
    login,
    logout,
    getAccessToken,
  };
};
